import { Button, ConfirmSheet, Input, PageHeader } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { OwnerDataTable as DataTable } from "../../components/owner-data-table.tsx";
import {
  useCreateZone,
  useDeleteZone,
  useOwnerState,
  useUpdateZone,
} from "../../features/backoffice/queries.ts";
import type { ZoneConfig } from "../../features/backoffice/types.ts";

export const Route = createFileRoute("/_app/zones")({ component: ZonesScreen });

function ZonesScreen() {
  const { data } = useOwnerState();
  const create = useCreateZone();
  const update = useUpdateZone();
  const del = useDeleteZone();
  const [pendingDelete, setPendingDelete] = useState<ZoneConfig | null>(null);
  const tables = data?.tables ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Zones"
        subtitle="CRUD zones with sort order and Around/floor grouping hints."
        actions={
          <Button
            onClick={() =>
              create.mutate({ name: "Balcony", nameTh: "ระเบียง", adjacencyHint: "near terrace" })
            }
          >
            Create zone
          </Button>
        }
      />
      {del.error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
          {del.error instanceof Error ? del.error.message : "Could not delete zone."}
        </p>
      ) : null}
      <DataTable
        rows={data?.zones ?? []}
        getRowKey={(row) => row.id}
        columns={[
          {
            key: "name",
            header: "Name",
            cell: (row) => (
              <div className="grid gap-2">
                <Input
                  aria-label={`${row.name} English name`}
                  defaultValue={row.name}
                  onBlur={(event) =>
                    event.target.value !== row.name &&
                    update.mutate({ zoneId: row.id, input: { name: event.target.value } })
                  }
                />
                <Input
                  aria-label={`${row.name} Thai name`}
                  defaultValue={row.nameTh}
                  onBlur={(event) =>
                    event.target.value !== row.nameTh &&
                    update.mutate({ zoneId: row.id, input: { nameTh: event.target.value } })
                  }
                />
              </div>
            ),
          },
          {
            key: "sort",
            header: "Sort",
            cell: (row) => (
              <Input
                aria-label={`${row.name} sort order`}
                type="number"
                defaultValue={row.sortOrder}
                onBlur={(event) =>
                  Number(event.target.value) !== row.sortOrder &&
                  update.mutate({
                    zoneId: row.id,
                    input: { sortOrder: Number(event.target.value) },
                  })
                }
              />
            ),
          },
          {
            key: "hint",
            header: "Adjacency hint",
            cell: (row) => (
              <Input
                aria-label={`${row.name} adjacency hint`}
                defaultValue={row.adjacencyHint}
                onBlur={(event) =>
                  event.target.value !== row.adjacencyHint &&
                  update.mutate({ zoneId: row.id, input: { adjacencyHint: event.target.value } })
                }
              />
            ),
          },
          {
            key: "tables",
            header: "Tables",
            cell: (row) => tables.filter((table) => table.zoneId === row.id).length,
          },
          {
            key: "actions",
            header: "Actions",
            cell: (row) => (
              <Button size="sm" variant="destructive" onClick={() => setPendingDelete(row)}>
                Delete
              </Button>
            ),
          },
        ]}
      />
      <ConfirmSheet
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name ?? "zone"}?`}
        description="Zones with tables cannot be deleted — move or delete the tables first. This cannot be undone."
        confirmLabel="Delete zone"
        destructive
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
    </div>
  );
}
